import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Provider } from 'react-redux';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { SnackbarProvider } from 'notistack';
import { QueryClient, QueryClientProvider } from 'react-query';
import { HelmetProvider } from 'react-helmet-async';

import { store } from './store';
import ErrorBoundary from './components/common/ErrorBoundary';
import PrivateRoute from './components/auth/PrivateRoute';
import PublicRoute from './components/auth/PublicRoute';

// Pages
import Home from './pages/Home';
import Login from './pages/Login';
import RegisterPage from './pages/RegisterPage';
import AboutPage from './pages/AboutPage';
import UKMapPage from './pages/UKMapPage';
import AdminPage from './pages/AdminPage';
import DashboardPage from './pages/DashboardPage';
import BloodPressurePage from './pages/BloodPressurePage';
import AnalyticsPage from './pages/AnalyticsPage';
import CommunityPage from './pages/CommunityPage';
import ProfilePage from './pages/ProfilePage';
import SettingsPage from './pages/SettingsPage';
import DevicesPage from './pages/DevicesPage';
import ExportPage from './pages/ExportPage';
import NotFoundPage from './pages/NotFoundPage';

// Layout
import Layout from './components/layout/Layout';

// Theme configuration
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
    },
    secondary: {
      main: '#dc004e',
      light: '#ff5983',
      dark: '#9a0036',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
    error: {
      main: '#f44336',
    },
    warning: {
      main: '#ff9800',
    },
    info: {
      main: '#2196f3',
    },
    success: {
      main: '#4caf50',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 300,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 300,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 400,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 400,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 500,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 500,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.43,
    },
  },
  shape: {
    borderRadius: 8,
  },
  spacing: 8,
});

// React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    },
  },
});

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <Provider store={store}>
          <ThemeProvider theme={theme}>
              <CssBaseline />
              <QueryClientProvider client={queryClient}>
                <SnackbarProvider
                  maxSnack={3}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  autoHideDuration={5000}
                >
                  <Router>
                    <Routes>
                      {/* Public Routes */}
                      <Route path="/" element={<Home />} />
                      <Route 
                        path="/login" 
                        element={
                          <PublicRoute>
                            <Login />
                          </PublicRoute>
                        } 
                      />
                      <Route 
                        path="/register" 
                        element={
                          <PublicRoute>
                            <RegisterPage />
                          </PublicRoute>
                        } 
                      />
                      <Route 
                        path="/about" 
                        element={
                          <PublicRoute>
                            <AboutPage />
                          </PublicRoute>
                        } 
                      />

                      {/* Private Routes */}
                      <Route
                        path="/dashboard"
                        element={
                          <PrivateRoute>
                            <Layout>
                              <DashboardPage />
                            </Layout>
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/blood-pressure"
                        element={
                          <PrivateRoute>
                            <Layout>
                              <BloodPressurePage />
                            </Layout>
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/analytics"
                        element={
                          <PrivateRoute>
                            <Layout>
                              <AnalyticsPage />
                            </Layout>
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/community"
                        element={
                          <PrivateRoute>
                            <Layout>
                              <CommunityPage />
                            </Layout>
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/profile"
                        element={
                          <PrivateRoute>
                            <Layout>
                              <ProfilePage />
                            </Layout>
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/settings"
                        element={
                          <PrivateRoute>
                            <Layout>
                              <SettingsPage />
                            </Layout>
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/devices"
                        element={
                          <PrivateRoute>
                            <Layout>
                              <DevicesPage />
                            </Layout>
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/export"
                        element={
                          <PrivateRoute>
                            <Layout>
                              <ExportPage />
                            </Layout>
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/uk-map"
                        element={
                          <PrivateRoute>
                            <Layout>
                              <UKMapPage />
                            </Layout>
                          </PrivateRoute>
                        }
                      />
                      <Route
                        path="/admin"
                        element={
                          <PrivateRoute>
                            <Layout>
                              <AdminPage />
                            </Layout>
                          </PrivateRoute>
                        }
                      />

                      {/* 404 Route */}
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                  </Router>
                </SnackbarProvider>
              </QueryClientProvider>
            </ThemeProvider>
        </Provider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App; 